import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCustomMountTransition } from '@/components/hooks/useCustomMountTransition';
import { useHistoryState } from '@/components/hooks/useHistoryState';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import {
  disposables,
  useMountTransition,
} from '@/components/hooks/useMountTransition';
import type { MetatagProps } from '@/components/layouts/custom-metatag';
import type { ExploreCarouselCardItemProps } from '@/components/sections/einstein/product/explore-carousel/card';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import {
  UspPopupFooter,
  UspPopupFooterCustomizeItem,
} from '@/components/shared/einstein/usp-popup/footer';
import { UspPopupHeader } from '@/components/shared/einstein/usp-popup/header';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Image } from '@/components/shared/media/image';
import { generateBannerImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import {
  clearBodyLockScroll,
  enableBodyLockScroll,
} from '@/components/shared/utility/lock-scroll';
import { normalisePathname } from '@/components/shared/utility/router';
import { config, keyframes, styled } from '@/stitches.config';

const StyledUspPopupContentWrapper = styled(Box, {
  position: 'relative',
  width: '$size-full',
  backgroundColor: '$black',
  borderTopRightRadius: '$rounded-4',
  borderTopLeftRadius: '$rounded-4',
  zIndex: '$1',
});

const StyledLoadingProgress = styled(Box, {
  position: 'absolute',
  bottom: '100%',
  width: '$size-full',
  height: '$size-1',
  backgroundColor: '$primary',
  transform: 'scaleX(var(--next-progress))',
  transformOrigin: 'left',
  willChange: 'transform',
  transition: 'var(--transition-progress)',
});

const AnimationModalAnimateSlideIn = keyframes({
  '0%': { opacity: 0.25, transform: 'translateY(100%)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

export type UspDetailType =
  | 'exterior'
  | 'interior'
  | 'power'
  | 'safety'
  | 'adas'
  | 'technology'
  | 'customize'
  | 'platform';

export interface UspDetailPopupItem extends ExploreCarouselCardItemProps {
  description: string;
  componentProps: any;
  headerImage: {
    desktop: ImageProps;
    mobile: ImageProps;
  };
}

export interface UspDetailData {
  key: UspDetailType;
  metatag?: MetatagProps;
  item: UspDetailPopupItem;
  order: number;
  Component: React.ComponentType<any>;
  customizeItem?: UspPopupFooterCustomizeItem;
}

export type UspDetailsComponentDataType = Partial<
  Record<UspDetailType, UspDetailData>
>;

const USP_MODAL_CLASSNAME = 'usp-detail-modal';

const Modal = dynamic(() => import('@/components/shared/overlays/modal'), {
  ssr: false,
});

interface UspPopupProps {
  isOpenFromSlider?: boolean;
  details: UspDetailsComponentDataType;
  usp?: UspDetailType;
  onClose?: (detail: UspDetailType) => void;
}
export interface UspPopupRef {
  openPopup: (detail: UspDetailType) => void;
  closePopup: (detail?: UspDetailType) => void;
}

export const UspPopup = forwardRef<UspPopupRef, UspPopupProps>(
  (
    { usp: uspType, details, isOpenFromSlider, onClose }: UspPopupProps,
    ref: any
  ) => {
    const router = useRouter();
    const pathname = usePathname();
    const { push, isBackPressed } = useHistoryState();
    const is2XL = useMediaQuery(config.media['2xl']);
    const isLG = useMediaQuery(config.media['lg']);
    const disposablesRef = useRef(disposables());

    const updateProgressRaf = useRef<number>(null);
    const isAnimateNextProgress = useRef(false);
    const activePopupElementRef = useRef<HTMLDivElement | null>(null);
    const progesssElementRef = useRef<HTMLDivElement | null>(null);

    const [openFromUsp, setOpenFromUsp] = useState<UspDetailData | undefined>(
      uspType ? details[uspType] : undefined
    );
    const [usp, setUsp] = useState<UspDetailData | undefined>(
      uspType ? details[uspType] : undefined
    );
    const [isOpen, setIsOpen] = useState<boolean>(!isOpenFromSlider);

    const loadedComponents = useRef<UspDetailData[]>(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      uspType ? [details[uspType]] : []
    );
    const [showBuyingTools, setShowBuyingTools] = useState<boolean>(false);

    const {
      shouldMount,
      startTransition: startTransitionOpen,
      trueFalseTransitionState: isOpenTrueFalseTransitionState,
    } = useMountTransition({
      isMounted: isOpen,
      unmountDelay: 600,
    });

    const {
      currentStateNextFrame: uspCurrentStateNextFrame,
      startTransition: startTransitionUsp,
      transitionState: uspTransitionState,
    } = useCustomMountTransition(usp, 800);

    const uspDetailKeys = useMemo(() => Object.keys(details), [details]);

    const memoNormalisedPathname = useMemo(
      () => normalisePathname(pathname),
      [pathname]
    );

    const memoNormalisedMainPathname = useMemo(
      () => `/${memoNormalisedPathname.split('/')[1]}`,
      [memoNormalisedPathname]
    );

    const shouldPreload = useMemo(
      () => openFromUsp?.key == usp?.key && !isOpenFromSlider,
      [openFromUsp, usp, isOpenFromSlider]
    );

    useImperativeHandle(
      ref,
      () => {
        return {
          openPopup,
          closePopup,
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const openPopup = (detail: UspDetailType) => {
      setOpenFromUsp(details[detail]);
      onPopupOpenStateAndRouteHandler(detail);
      startTransitionOpen(() => setIsOpen(true));
      startTransitionUsp(() => setUsp(details[detail]));
      enableBodyLockScroll({ onlyOverflow: true });
    };

    const closePopup = (detail?: UspDetailType) => {
      loadedComponents.current = [];
      startTransitionUsp(() => setUsp(undefined));
      startTransitionOpen(() => setIsOpen(false));

      if (!isOpenFromSlider) {
        if (detail === 'customize') {
          router.push(`${memoNormalisedMainPathname}?section=customize`);
        } else {
          router.push(memoNormalisedMainPathname, {
            scroll: false,
          });
        }
        return;
      }

      clearBodyLockScroll();
      push(memoNormalisedMainPathname, {
        shallow: true,
      });
      onClose?.(detail as UspDetailType);
    };

    const renderComponent = (componentData: UspDetailData) => {
      const { item, Component } = componentData;
      return <Component {...item.componentProps} />;
    };

    const onPopupOpenStateAndRouteHandler = (detail: UspDetailType) => {
      const alreadyHadComponent = loadedComponents.current.some(
        (item) => item?.key === detail
      );

      if (!isOpenFromSlider) {
        if (alreadyHadComponent) return;
        loadedComponents.current.push(details[detail] as UspDetailData);
        return;
      }

      if (alreadyHadComponent) {
        push(`${memoNormalisedPathname}/${detail}`, {
          shallow: true,
        });
        return;
      }
      loadedComponents.current.push(details[detail] as UspDetailData);
      push(`${memoNormalisedPathname}/${detail}`, {
        shallow: true,
      });
    };

    const startNextSectionCountdown = () => {
      const start = performance.now();
      const DURATION = 6000; // 6s
      const updateProgressBar = () => {
        if (!progesssElementRef.current) return;

        const now = performance.now();
        const delta = Math.min((now - start) / DURATION, 1);

        if (delta < 1) {
          progesssElementRef.current.style.setProperty(
            '--next-progress',
            `${delta}`
          );
          updateProgressRaf.current = requestAnimationFrame(updateProgressBar);
        } else {
          progesssElementRef.current.style.setProperty('--next-progress', '1');
          isAnimateNextProgress.current = false;
          goNextSection();
        }
      };
      updateProgressBar();
    };

    const animateScrollProgress = ({
      scrollY,
      scrollEndPosition,
    }: {
      scrollY: number;
      scrollEndPosition: number;
    }) => {
      if (scrollY >= scrollEndPosition && !isAnimateNextProgress.current) {
        isAnimateNextProgress.current = true;
        progesssElementRef.current?.style.setProperty(
          '--transition-progress',
          'none'
        );
        progesssElementRef.current?.style.setProperty('opacity', '1');
        startNextSectionCountdown();
      } else if (isAnimateNextProgress.current && scrollY < scrollEndPosition) {
        isAnimateNextProgress.current = false;
        cancelAnimationFrame(updateProgressRaf.current || 0);
        progesssElementRef.current?.style.setProperty(
          '--transition-progress',
          'transform 500ms ease'
        );
        progesssElementRef.current?.style.setProperty('--next-progress', '0');
      }
    };

    const showBuyingToolsHandler = ({
      scrollY,
      scrollEndPosition,
    }: {
      scrollY: number;
      scrollEndPosition: number;
    }) => {
      if (
        showBuyingTools &&
        (scrollY >= scrollEndPosition || scrollY <= window.innerHeight / 2)
      ) {
        setShowBuyingTools(false);
      } else if (
        scrollY > window.innerHeight / 2 &&
        scrollY < scrollEndPosition
      ) {
        setShowBuyingTools(true);
      }
    };

    const handleScroll = (y: number) => {
      if (!activePopupElementRef.current) return;
      const paddingTop = is2XL ? 80 : 40;
      const footerPaddingTop = isLG ? 94 : 76;
      const footerHeight = isLG ? 244 : 196;
      const scrollEndPosition =
        activePopupElementRef.current.scrollHeight -
        paddingTop +
        footerHeight -
        window.innerHeight; // for safe area web view browser
      const scrollEndPositionProgressBar =
        activePopupElementRef.current.scrollHeight +
        footerHeight +
        paddingTop -
        footerPaddingTop -
        window.innerHeight +
        25;
      const scrollY = Math.floor(y);
      animateScrollProgress({
        scrollY,
        scrollEndPosition: scrollEndPositionProgressBar,
      });

      if (!is2XL) {
        showBuyingToolsHandler({
          scrollY,
          scrollEndPosition,
        });
      }
    };

    const handleLoadNextSection = (nextSection: UspDetailData) => {
      loadedComponents.current.push(nextSection);
      push(`${memoNormalisedMainPathname}/${nextSection.key}`, {
        shallow: true,
      });
    };

    const goNextSection = () => {
      cancelAnimationFrame(updateProgressRaf.current || 0);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const nextSection = details[uspDetailKeys[usp.order + 1]];
      if (!nextSection) {
        closePopup('customize');
        return;
      }

      startTransitionUsp(() => setUsp(nextSection));
      progesssElementRef.current?.style.setProperty(
        '--transition-progress',
        'transform 500ms ease'
      );
      progesssElementRef.current?.style.setProperty('--next-progress', '1');
      disposablesRef.current.setTimeout(() => {
        progesssElementRef.current?.style.setProperty('opacity', '0');
      }, 100);
      handleLoadNextSection(nextSection);
      disposablesRef.current.setTimeout(() => {
        document.querySelector(`.${USP_MODAL_CLASSNAME}`)?.scrollTo({
          top: 0,
        });
      }, 800);
    };

    const getUspTransitionStageHeader = () => {
      if (openFromUsp?.key === usp?.key) return 'enter first';
      return uspTransitionState?.key === uspCurrentStateNextFrame?.key
        ? 'enter'
        : '';
    };

    useEffect(() => {
      if (!isBackPressed) return;

      const nativePathnameSplits = window.location.pathname.split('/');
      if (isOpen) {
        closePopup(usp?.key);
        disposablesRef.current.dispose();
      } else if (
        nativePathnameSplits.length >= 2 &&
        pathname !== window.location.pathname
      ) {
        const uspKey = nativePathnameSplits[
          nativePathnameSplits.length - 1
        ] as UspDetailType;
        openPopup(uspKey);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBackPressed, pathname]);

    useEffect(() => {
      if (!isOpenFromSlider) {
        enableBodyLockScroll({ onlyOverflow: true });
      }
    }, [isOpenFromSlider]);

    return (
      <>
        {!!shouldPreload && (
          <Image
            src={usp?.item.headerImage.desktop.src as string}
            alt={usp?.item.headerImage.desktop.alt as string}
            width={usp?.item.headerImage.desktop.width as number}
            height={usp?.item.headerImage.desktop.height as number}
            css={{
              opacity: 0,
              visibility: 'hidden',
            }}
            layout="fill"
            sources={generateBannerImageSources({
              mobileSrc: usp?.item.headerImage.mobile.src,
              mobileOptions: {
                crop: '9:16',
              },
              desktopSrc: usp?.item.headerImage.desktop.src,
              desktopOptions: {
                crop: '16:9',
              },
            })}
            priority
          />
        )}
        {!!shouldMount && (
          <Modal
            size="lg"
            type="main"
            open={shouldMount}
            onClose={() => closePopup(usp?.key)}
            onScrollY={handleScroll}
            cssDialog={{
              opacity: isOpenTrueFalseTransitionState ? 1 : 0,
              transition: 'opacity 600ms cubic-bezier(0.17,0.84,0.44,1)',
              willChange: 'opacity',
              overflowY:
                uspTransitionState?.key && uspTransitionState?.key !== usp?.key
                  ? 'hidden !important'
                  : 'undefined',
              paddingTop: '$space-10',
              animation: usp
                ? `${AnimationModalAnimateSlideIn} 600ms cubic-bezier(0.17,0.84,0.44,1)`
                : '',
              animationFillMode: 'both',
              animationDelay: '500ms',
              '@2xl': {
                paddingTop: '$space-20',
              },
            }}
            cssBackdrop={{
              pointerEvents: isOpenTrueFalseTransitionState ? 'all' : 'none',
              opacity: isOpenTrueFalseTransitionState ? '1' : '0',
              transitionProperty: 'opacity',
              transitionDuration: isOpenTrueFalseTransitionState
                ? '600ms'
                : '200ms',
              willChange: 'opacity',
            }}
            className={USP_MODAL_CLASSNAME}
            uspModal
          >
            {!!usp && (
              <>
                <Flex
                  justifyContent="end"
                  css={{
                    position: 'sticky',
                    top: '-$space-10',
                    paddingRight: '$space-4',
                    zIndex: '$popup',
                    width: '$size-full',
                    '@2xl': {
                      top: '-$space-20',
                    },
                    transition: usp
                      ? uspTransitionState?.key &&
                        uspTransitionState?.key !== usp?.key
                        ? 'undefined'
                        : 'opacity 600ms var(--transition-easing)'
                      : undefined,
                    willChange: 'opacity',
                    visibility: usp ? 'visible' : 'hidden',
                    opacity:
                      uspTransitionState?.key &&
                      uspTransitionState?.key !== usp?.key
                        ? 0
                        : 1,
                  }}
                >
                  <ButtonIcon
                    size="md"
                    icon="close"
                    variant="button-icon-color-gray-dark"
                    onClick={() => closePopup(usp?.key)}
                    test={{ dataTest: 'modalHeader_closeButton' }}
                    css={{
                      position: 'absolute',
                      marginTop: '$4',
                    }}
                    tracking={{
                      dataTrack: 'usp_popup_close_button',
                      dataTrackSection: usp?.item.menu,
                      dataTrackValue: usp?.item.menu,
                      dataTrackText: usp?.item.title,
                    }}
                  />
                </Flex>

                <StyledUspPopupContentWrapper
                  ref={activePopupElementRef}
                  css={{
                    visibility:
                      usp?.key !== uspTransitionState?.key &&
                      usp?.key !== openFromUsp?.key
                        ? 'hidden'
                        : 'visible',
                  }}
                >
                  <UspPopupHeader
                    item={usp?.item}
                    className={getUspTransitionStageHeader()}
                  />
                  {!!uspTransitionState?.key &&
                    uspTransitionState?.key === usp?.key &&
                    renderComponent(usp)}
                </StyledUspPopupContentWrapper>

                <UspPopupFooter
                  item={
                    details[uspDetailKeys[uspTransitionState?.order + 1]]?.item
                  }
                  parentItem={usp?.item}
                  className={
                    usp?.key !== uspTransitionState?.key &&
                    usp?.key !== openFromUsp?.key
                      ? 'usp-footer leave'
                      : 'usp-footer'
                  }
                  customizeItem={details[usp?.key]?.customizeItem}
                  onNext={goNextSection}
                />

                <Box
                  ref={progesssElementRef as React.Ref<HTMLDivElement>}
                  position="sticky"
                  bottom="0"
                  left="0"
                  width="full"
                  zIndex="floating"
                  pointerEvents="none"
                  css={{
                    transition: 'opacity 600ms ease-in-out',
                    '--next-progress': 0,
                    transform: 'translateZ(0)',
                  }}
                >
                  <StyledLoadingProgress />
                </Box>
              </>
            )}
          </Modal>
        )}
      </>
    );
  }
);

UspPopup.displayName = 'EinsteinUspPopup';
